import React from "react";
import ProCard from "@ant-design/pro-card";
import { CardProps } from "@ant-design/pro-card/lib/type";
import styled from "styled-components";
import { InformationCircleIcon } from "@hugeicons/core-free-icons";
import HugeIcon from "../HugeIcon";
import CardTitle from "../CardTitle";
import { colors } from "src/theme/colors";
import { line, radii, shadow, space } from "src/theme/ui";
import "./index.css";

interface Props extends CardProps {
  border?: boolean;
  /** A small tinted glyph that gives the card an identity at a glance. */
  icon?: React.ReactNode;
}

export const cardSurface = `
  display: flex;
  flex-direction: column;
  border-radius: ${radii.lg}px;
  border: 1px solid ${line.hairline};
  box-shadow: ${shadow.card};
  background: ${colors.white};

  .ant-pro-card-header {
    padding: ${space.lg}px ${space.xl}px 0;
    border-bottom: 0;
    /* Centred so a title and whatever sits opposite it — a segmented
       control, a select — share a centre line. Top-aligning them lines up
       their boxes, not their content, and a 22px title against a 34px
       control then reads as crooked. The reserved height below keeps the
       body's start fixed either way. */
    align-items: center;
    /* Exactly the height of the tallest thing a header holds — the
       segmented control — so a card WITH one and a card without put their
       titles at the same height, and that height is the padding above them.
       At 50 the row was taller than anything in it, and centring spent the
       surplus above the title: every card in the app opened with a gap half
       again as deep as its own margins. */
    min-height: 44px;
  }
  .ant-pro-card-title {
    display: flex;
    align-items: center;
    overflow: visible;
  }
  /* Fill the card so a body can pin its own footer to the bottom edge. */
  .ant-pro-card-body {
    display: flex;
    flex-direction: column;
    flex: 1;
    min-height: 0;
    padding: ${space.sm}px ${space.xl}px ${space.lg}px;
  }
  .ant-pro-core-label-tip-icon,
  .ant-pro-card-title .anticon {
    color: ${colors.gray[400]};
    font-size: 14px;
    margin-left: 0;
    transition: color 0.15s ease;
  }
  .ant-pro-core-label-tip-icon:hover {
    color: ${colors.gray[600]};
  }
`;

const StyledProCard = styled(ProCard)`
  ${cardSurface}
`;

/* The card's mark, in the rail's idiom: a quiet stroked glyph and nothing
   around it.

   This was a tinted 36px tile, which is what football puts on a KPI card —
   but football's are small tiles in a dense strip, where the chip is what
   separates one from the next. At this size, three filled blue tiles down
   the page became the first thing the eye landed on, and the page wanted
   the same restraint as the sidebar: the glyph names the card, the accent
   is saved for the one thing that is actually being marked. */
export const IconBadge = styled.span`
  display: inline-flex;
  flex: none;
  align-items: center;
  justify-content: center;
  margin-right: 9px;
  color: ${colors.gray[500]};

  /* Sized here rather than at each call site, so every card's mark is the
     same regardless of what its own icon was asked for. */
  svg {
    width: 17px;
    height: 17px;
  }
`;

/* Ant Design's pro-components default the tooltip's trigger to their own
   info glyph. It takes an icon, so every card passes ours — written here
   once rather than at each call site, which also keeps `tooltip="..."` a
   plain string for the cards themselves. */
const withOurTipIcon = (tooltip: Props["tooltip"]) =>
  typeof tooltip === "string"
    ? {
        title: tooltip,
        icon: <HugeIcon icon={InformationCircleIcon} size={14} />,
      }
    : tooltip;

const DashboardCard: React.FC<Props> = ({
  className,
  border = true,
  icon,
  ...props
}) => {
  return (
    <StyledProCard
      {...props}
      tooltip={withOurTipIcon(props.tooltip)}
      className={`${className ?? ""} card`}
      title={
        <>
          {icon && <IconBadge>{icon}</IconBadge>}
          <CardTitle>{props.title}</CardTitle>
        </>
      }
      bodyStyle={props.bodyStyle}
    >
      {props.children}
    </StyledProCard>
  );
};

export default DashboardCard;
